/**
 * Resolve the base URL for app API calls (`/api/*` — Next.js routes hosted
 * on the Core API site; the Pro app's fitting-briefing endpoints live there
 * too). Mirrors the pattern in `apps/core/.../src/lib/apiBase.ts`.
 *
 * Web: returns `''` so `${base}/api/...` becomes a same-origin relative
 *   path — the Netlify deploy proxies these to the Core API site.
 *
 * Capacitor: same-origin is `capacitor://localhost`, which can't reach the
 *   API. Prefer `VITE_API_BASE_URL` when set, but reject loopback values
 *   (a baked-in `http://localhost:3002` is unreachable on a real device);
 *   fall back to the deployed Core API host.
 */
/** Production Core API (Railway). See Core apiBase.ts for rationale. */
const GOLF_AUTH_API_PRODUCTION = 'https://app.golfauth.com';

/** Legacy Netlify Core API — golfauthredesign previews only. */
const GOLF_AUTH_CORE_API_NETLIFY = 'https://golfauth-core-api.netlify.app';

function normalizeApiOrigin(raw: string): string {
  let u = raw.trim().replace(/\/+$/, '');
  u = u.replace(/\/api$/i, '');
  u = u.replace(/\/core$/i, '');
  return u.replace(/\/+$/, '');
}

const isLoopbackUrl = (u: string) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?(\/|$)/i.test(u);

function isCapacitorNative(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as Window & { Capacitor?: { getPlatform?: () => string } };
  const p = w.Capacitor?.getPlatform?.();
  return p === 'ios' || p === 'android';
}

function isFittingWebHost(hostname: string): boolean {
  return (
    hostname === 'golfauth.com' ||
    hostname === 'www.golfauth.com' ||
    hostname === 'staging.golfauth.com'
  );
}

export function getApiBase(): string {
  const envRaw = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  const envBase = envRaw.trim() ? normalizeApiOrigin(envRaw) : '';

  if (isCapacitorNative()) {
    if (envBase && !isLoopbackUrl(envBase)) return envBase;
    return GOLF_AUTH_API_PRODUCTION;
  }

  // Apex SPA (prod + staging): same-origin /api/* via the Cloudflare
  // Worker proxy. Ignore baked VITE_API_BASE_URL (staging CI sets it to
  // staging-app.golfauth.com which is cross-origin and used to fail CORS).
  if (typeof window !== 'undefined' && isFittingWebHost(window.location.hostname)) {
    return '';
  }

  // Reject loopback URLs on web too. A stale local-dev
  // VITE_API_BASE_URL=http://localhost:3002 in the build env would
  // otherwise bake `http://localhost:3002` into the production bundle
  // and every /api/* call from real users would ERR_CONNECTION_REFUSED
  // — reproduced 2026-07-03 on the marketplace photo upload. Falling
  // through to '' means same-origin /api/* via the CF Worker proxy,
  // which is the correct prod behaviour anyway.
  if (envBase && !isLoopbackUrl(envBase)) return envBase;

  // Web: same-origin relative paths. CF Worker proxies /api/* to the
  // Core API host — see workers/static-assets-router.mjs.
  return '';
}
